import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import moment from 'moment';
import { MarkdownModule } from 'ngx-markdown';
import { Subject, skip, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import { AlbertaIconModule } from '../shared/components/alberta-icon/alberta-icon.module';
import { LlmIntentService, ThinkingStep } from './llm-intent.service';
import { Chat } from './model/chat.model';
import { MessageIntegratedCare, MessagePatient, MessagePayer } from './model/message.model';
import { RealtimeSttService } from './realtime-stt.service';

@Component({
  selector: 'itl-chat-assistant',
  templateUrl: './chat-assistant.component.html',
  styleUrls: ['./chat-assistant.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [DatePipe, CommonModule, MarkdownModule, AlbertaIconModule],
})
export class ChatAssistantComponent implements OnInit {
  @Output() backButtonPressed = new EventEmitter();
  @ViewChild('messageInput') messageInput: ElementRef;

  // Input to control visibility and manage subscriptions
  @Input() set isVisible(visible: boolean) {
    if (visible) {
      this.setupVoiceSubscriptions();
    } else {
      this.cleanupVoiceServices();
      this.resetChat();
      // Clear subscriptions so they can be recreated on next open
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
    }
  }

  chat: Chat;

  // Voice functionality properties
  partialText = '';
  isListening = false;
  thinkingSteps: ThinkingStep[] = [];
  private readonly processedUtteranceIds = new Set<string>();

  private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    private readonly _sttService: RealtimeSttService,
    private readonly _llmIntentService: LlmIntentService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Decorated methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resize on 'input' and 'ngModelChange' events
   *
   * @private
   */
  @HostListener('input')
  @HostListener('ngModelChange')
  private _resizeMessageInput(): void {
    // This doesn't need to trigger Angular's change detection by itself
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        // Set the height to 'auto' so we can correctly read the scrollHeight
        this.messageInput.nativeElement.style.height = 'auto';

        // Detect the changes so the height is applied
        this._changeDetectorRef.detectChanges();

        // Get the scrollHeight and subtract the vertical padding
        this.messageInput.nativeElement.style.height = `${this.messageInput.nativeElement.scrollHeight}px`;

        // Detect the changes one more time to apply the final height
        this._changeDetectorRef.detectChanges();
      });
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.resetChat();
  }

  cleanupVoiceServices() {
    // Clean up voice services
    this._sttService.stopSession();
    this._llmIntentService.clearHistory();
    this.isListening = false;
  }

  handleEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  resetChat(): void {
    this.chat = {
      messages: [],
      isBotTyping: false,
    };
    this.addMessage('Wie kann ich dir helfen?', false);
  }

  sendMessage(): void {
    const text = this.messageInput.nativeElement.value;
    this.messageInput.nativeElement.value = '';
    this.messageInput.nativeElement.focus();
    this.sendVoiceMessage(text);
    // this.addMessage(text, true);
    // this.chat.isBotTyping = true;
    // this._chatAssistantService.sendMessage(text).subscribe((response: any) => {
    //   this.chat.isBotTyping = false;
    //   this.addMessage(response.message, false);
    // });
    // this._changeDetectorRef.markForCheck();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  addMessage(
    message: string,
    isMine: boolean,
    patients?: MessagePatient[],
    integratedCares?: MessageIntegratedCare[],
    payers?: MessagePayer[]
  ): void {
    this.chat.messages.push({
      id: v4(),
      isMine,
      value: message,
      createdAt: moment().toISOString(),
      patients,
      integratedCares,
      payers,
    });
    this._changeDetectorRef.markForCheck();
  }

  async selectPayer(messageId: string, payer: MessagePayer): Promise<void> {
    const msg = this.chat.messages.find(m => m.id === messageId);
    if (msg) {
      msg.payers = undefined;
    }
    await this.sendVoiceMessage(`Krankenkasse: ${payer.name} (IK: ${payer.ikNumber ?? payer._id})`);
  }

  async selectPatient(messageId: string, patient: MessagePatient): Promise<void> {
    const msg = this.chat.messages.find(m => m.id === messageId);
    if (msg) {
      msg.patients = undefined;
    }
    const birthday = patient.birthday ? new Date(patient.birthday).toLocaleDateString('de-DE') : '';
    const label = `${patient.firstName} ${patient.lastName}${birthday ? ', ' + birthday : ''}`;
    await this.sendVoiceMessage(`${label}`);
  }

  async selectIntegratedCare(messageId: string, integratedCare: MessageIntegratedCare): Promise<void> {
    const msg = this.chat.messages.find(m => m.id === messageId);
    if (msg) {
      msg.integratedCares = undefined;
    }
    const label = integratedCare.locationName
      ? `${integratedCare.therapyName} (${integratedCare.therapyTypeName}) – ${integratedCare.locationName}`
      : `${integratedCare.therapyName} (${integratedCare.therapyTypeName})`;
    await this.sendVoiceMessage(`Versorgung: ${label} (ID: ${integratedCare._id})`);
  }

  close() {
    this.backButtonPressed.emit();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Voice functionality methods
  // -----------------------------------------------------------------------------------------------------

  private setupVoiceSubscriptions(): void {
    this._sttService.partial$.pipe(takeUntil(this._unsubscribeAll)).subscribe(text => {
      this.partialText = text;
      this._changeDetectorRef.markForCheck();
    });

    this._llmIntentService.thinkingStep$.pipe(takeUntil(this._unsubscribeAll)).subscribe((step: ThinkingStep) => {
      const existing = this.thinkingSteps.find(s => s.tool === step.tool);
      if (existing) {
        existing.status = step.status;
      } else {
        this.thinkingSteps.push({ ...step });
      }
      this._changeDetectorRef.markForCheck();
    });

    // Automatically send when transcription is completed
    // Skip(1) to ignore the initial BehaviorSubject value when component loads
    this._sttService.completed$.pipe(skip(1), takeUntil(this._unsubscribeAll)).subscribe(utterance => {
      if (utterance?.text && utterance.id && !this.processedUtteranceIds.has(utterance.id)) {
        this.processedUtteranceIds.add(utterance.id);
        this.sendVoiceMessage(utterance.text);
      }
    });
  }

  async toggleMic(): Promise<void> {
    if (this.isListening) {
      this._sttService.stopSession();
      this.isListening = false;
    } else {
      await this._sttService.startSession();
      this.isListening = true;
    }
    this._changeDetectorRef.markForCheck();
  }

  private async sendVoiceMessage(text: string): Promise<void> {
    // Add user message to chat
    this.addMessage(text, true);

    this.chat.isBotTyping = true;
    this._changeDetectorRef.markForCheck();

    try {
      this.thinkingSteps = [];
      const response = await this._llmIntentService.processUtterance(text);

      // Add assistant response to chat
      if (response?.reply) {
        const patients = response.patients && response.patients.length > 1 ? response.patients : undefined;
        const integratedCares =
          response.integratedCares && response.integratedCares.length > 1 ? response.integratedCares : undefined;
        const payers = response.payers && response.payers.length > 1 ? response.payers : undefined;
        this.addMessage(
          response.reply,
          false,
          patients as MessagePatient[],
          integratedCares as MessageIntegratedCare[],
          payers
        );
      }

      // Navigate to patient if needed
      if (response?.patients && response.patients.length > 0) {
        // Optionally auto-navigate to first patient or show patient buttons
        // For now, we'll just show the patient info in the message
      }
    } catch (error) {
      console.error('Error processing voice message:', error);
      this.addMessage('Sorry, there was an error processing your message.', false);
    } finally {
      this.chat.isBotTyping = false;
      this.thinkingSteps = [];
      this._changeDetectorRef.markForCheck();
    }
  }
}
